import { Channel } from '@vendorflow/types'
import { useQueryClient } from '@tanstack/react-query'
import { useMyOrgs } from 'queries/Auth.queries'
import { useEvent } from 'hooks/Utils.hooks'
import {
  channelQueryFn,
  channelQueryKey,
  CHANNELS_STALE_TIME,
  channelsQueryFn,
  channelsQueryKey,
} from 'queries/Channels.queries'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import { noop } from 'lodash'
import { useParams } from 'react-router-dom'
import { WorkspaceParams } from 'types/Router.types'
import { useEffect, useRef } from 'react'

export function useRedirectToOrgAndChannelBase({
  successFn,
  errorFn,
  finallyFn,
}: {
  successFn: (envy: string, channel: Channel) => void
  errorFn?: (e: Error) => void
  finallyFn?: () => void
}) {
  const queryClient = useQueryClient()
  const params = useParams<WorkspaceParams>()
  const workspaces = useMyOrgs()
  const selectedEnvy = useRef<string | null>(params.envy || null)

  useEffect(() => {
    if (!workspaces.length) {
      return
    }

    if (!selectedEnvy.current) {
      selectedEnvy.current = params.envy || workspaces[0].id
    }
  }, [params.envy, workspaces])

  return useEvent(async (channelId) => {
    if (!channelId) {
      return
    }

    queryClient
      .fetchQuery(channelQueryKey(channelId), channelQueryFn)
      .then((result) => {
        Promise.all(
          workspaces.map((ws) =>
            queryClient.fetchQuery(channelsQueryKey(QueryCacheEnum.ORGANIZATIONS, ws.id), channelsQueryFn, {
              staleTime: CHANNELS_STALE_TIME,
            })
          )
        )
          .then((results) => {
            for (let index = 0; index < results.length; index++) {
              const found = results[index].find((c) => c.id === channelId)
              if (found && workspaces[index].id !== params.envy) {
                selectedEnvy.current = workspaces[index].id
              }
            }
            successFn(selectedEnvy.current!, result.channel)
          })
          .catch(errorFn ? errorFn : noop)
          .finally(finallyFn ? finallyFn : noop)
      })
      .catch(errorFn)
  })
}
